import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Utensils, BookOpen, Volume2, VolumeX } from "lucide-react";
import { MaskedLines, FadeIn } from "@/components/motion";
import { Marquee } from "@/components/Marquee";
import { PROGRAMS, STATS, CHAPTERS, usePageMeta } from "@/lib/site";

const Home = () => {
  usePageMeta(
    "Hope Centers International — Feeding & Educating Children",
    "Hope Centers stands in the gap for children in need: 27 feeding centers and the Dream Release education program across the Philippines and beyond."
  );

  const heroVideoRef = useRef(null);
  const [heroMuted, setHeroMuted] = useState(true);

  const toggleHeroSound = () => {
    const v = heroVideoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setHeroMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };

  useEffect(() => {
    const v = heroVideoRef.current;
    if (v) {
      v.muted = true;
      v.play().catch(() => {});
    }
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-32 md:pt-44 pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn y={16}>
            <p className="label" data-testid="home-hero-label">
              Hope Centers International — Philippines &amp; Lithuania
            </p>
          </FadeIn>
          <h1
            data-testid="home-hero-heading"
            className="mt-6 font-serif font-medium text-ink text-5xl sm:text-6xl md:text-7xl lg:text-[6rem] tracking-tighter leading-[0.98]"
          >
            <MaskedLines
              delay={0.15}
              lines={[
                <>Full plates,</>,
                <>open books,</>,
                <>
                  <em className="text-terra">bright futures.</em>
                </>,
              ]}
            />
          </h1>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
            <FadeIn delay={0.55} className="md:col-span-6 lg:col-span-5">
              <p className="text-base sm:text-lg leading-relaxed text-ink/75 max-w-md">
                We stand in the gap for children in need — feeding and educating communities across the Philippines and
                Lithuania, one neighborhood at a time.
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Link to="/donate" data-testid="home-hero-donate-button" className="btn-primary group">
                  Donate today
                  <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link to="/programs" data-testid="home-hero-programs-button" className="btn-outline group">
                  Explore programs
                  <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </Link>
              </div>
            </FadeIn>
            <FadeIn delay={0.7} className="hidden md:block md:col-span-6 lg:col-span-7">
              <p className="font-serif italic text-2xl text-ink/50 text-right leading-snug">
                “A meal is never just a meal.
                <br />
                It is proof that someone stayed.”
              </p>
            </FadeIn>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-14 md:mt-20">
          <FadeIn delay={0.2}>
            <div className="relative">
              <div className="relative overflow-hidden rounded-2xl aspect-[4/3] sm:aspect-[16/9] lg:aspect-[21/10] bg-forest">
                <video
                  ref={heroVideoRef}
                  data-testid="home-hero-video"
                  className="absolute inset-0 w-full h-full object-cover"
                  poster="/images/hero.jpg"
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="auto"
                  aria-label="Hope Centers promo video"
                >
                  <source src="/videos/promo.webm" type="video/webm" />
                  <source src="/videos/promo.mp4" type="video/mp4" />
                </video>
              </div>
              <div
                data-testid="home-hero-stat-chip"
                className="absolute bottom-5 left-5 sm:bottom-8 sm:left-8 bg-paper rounded-full pl-4 pr-6 py-3 flex items-center gap-3 shadow-sm"
              >
                <span className="grid place-items-center w-9 h-9 rounded-full bg-marigold/20 text-marigold">
                  <Utensils size={16} />
                </span>
                <span className="text-sm font-bold text-ink">
                  27 feeding centers <span className="font-normal text-ink-soft">· 2 countries</span>
                </span>
              </div>
              <button
                type="button"
                data-testid="home-hero-sound-toggle"
                onClick={toggleHeroSound}
                aria-label={heroMuted ? "Unmute promo video" : "Mute promo video"}
                className="absolute bottom-5 right-5 sm:bottom-8 sm:right-8 grid place-items-center w-12 h-12 rounded-full bg-paper/90 backdrop-blur text-ink shadow-sm transition-[background-color,transform] duration-300 hover:bg-paper hover:scale-105"
              >
                {heroMuted ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
            </div>
          </FadeIn>
        </div>
      </section>

      <Marquee />

      {/* FEATURED PROGRAMS — bento */}
      <section className="py-24 md:py-32 lg:py-36">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-wrap items-end justify-between gap-6 mb-14 md:mb-20">
            <FadeIn>
              <p className="label">What we do</p>
              <h2 className="mt-4 font-serif font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-xl">
                Two programs. One promise.
              </h2>
            </FadeIn>
            <FadeIn delay={0.15}>
              <Link
                to="/programs"
                data-testid="home-programs-all-link"
                className="group inline-flex items-center gap-2 text-sm font-bold text-terra"
              >
                See all programs
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </FadeIn>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10">
            {PROGRAMS.map((p, i) => (
              <FadeIn key={p.slug} delay={i * 0.12} className={i === 0 ? "md:col-span-7" : "md:col-span-5"}>
                <Link
                  to={`/programs#${p.slug}`}
                  data-testid={`home-program-card-${p.slug}`}
                  className="group block h-full bg-white border border-ink/10 rounded-2xl overflow-hidden transition-[border-color,transform] duration-500 hover:border-terra/40 hover:-translate-y-1"
                >
                  <div className="overflow-hidden">
                    <img
                      src={p.cardImage}
                      alt={p.cardAlt}
                      loading="lazy"
                      className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                        i === 0 ? "aspect-[16/9]" : "aspect-[16/10]"
                      }`}
                    />
                  </div>
                  <div className="p-7 md:p-9">
                    <p className="font-mono text-xs uppercase tracking-[0.25em] text-terra">Program {p.number}</p>
                    <h3 className="mt-3 font-serif text-3xl md:text-4xl font-medium tracking-tight">{p.name}</h3>
                    <p className="mt-3 text-ink/70 leading-relaxed">
                      {p.slug === "feeding-centers" ? "" : `${p.tagline} `}
                      {p.intro.split(".")[0]}.
                    </p>
                    <span className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-ink transition-colors duration-300 group-hover:text-terra">
                      Learn more
                      <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </FadeIn>
            ))}

            <FadeIn delay={0.1} className="md:col-span-5">
              <div className="h-full bg-paper-deep border border-ink/10 rounded-2xl p-8 md:p-10 flex flex-col justify-between min-h-[240px]">
                <span className="grid place-items-center w-12 h-12 rounded-full bg-terra/10 text-terra">
                  <BookOpen size={20} />
                </span>
                <div>
                  <p className="font-serif text-3xl md:text-4xl font-medium tracking-tight leading-tight">
                    $25 covers a month of school meals for one child.
                  </p>
                  <p className="mt-4 text-ink/60 text-sm">Small gifts, compounded daily, become childhoods.</p>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} className="md:col-span-7">
              <div className="h-full bg-forest text-paper rounded-2xl p-8 md:p-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 min-h-[240px]">
                <div>
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-paper/50">Give</p>
                  <p className="mt-3 font-serif text-3xl md:text-4xl font-medium tracking-tight leading-tight max-w-md">
                    Give once — or walk with us monthly.
                  </p>
                </div>
                <Link
                  to="/donate"
                  data-testid="home-bento-donate-button"
                  className="shrink-0 inline-flex items-center gap-2 rounded-full bg-marigold px-7 py-3.5 text-sm font-bold text-ink transition-[background-color,transform] duration-300 hover:bg-paper hover:scale-105"
                >
                  Start giving
                  <ArrowRight size={16} />
                </Link>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* IMPACT — dark band */}
      <section className="bg-forest text-paper py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-paper/50">The impact</p>
            <h2 className="mt-4 font-serif font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-2xl">
              Hope you can count.
            </h2>
          </FadeIn>
          <div className="mt-14 md:mt-20 grid grid-cols-2 lg:grid-cols-4 gap-y-12 gap-x-8">
            {STATS.map((s, i) => (
              <FadeIn key={s.label} delay={i * 0.1}>
                <p data-testid={`home-stat-value-${i}`} className="font-serif text-6xl md:text-7xl font-medium tracking-tight text-marigold">
                  {s.value}
                </p>
                <p className="mt-3 text-sm text-paper/65 leading-relaxed max-w-[180px]">{s.label}</p>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="py-24 md:py-32 lg:py-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn className="mb-16 md:mb-24">
            <p className="label">Our manifesto</p>
            <h2 className="mt-4 font-serif font-medium text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              How hope gets done.
            </h2>
          </FadeIn>
          <div className="space-y-16 md:space-y-24">
            {CHAPTERS.map((c, i) => (
              <FadeIn key={c.number} delay={0.05}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12 items-start border-t border-ink/10 pt-10 md:pt-14">
                  <div className="md:col-span-4">
                    <span
                      data-testid={`home-chapter-number-${i}`}
                      className="font-serif text-7xl md:text-8xl font-semibold leading-none"
                      style={{ WebkitTextStroke: "1.5px #EC4849", color: "transparent" }}
                    >
                      {c.number}
                    </span>
                  </div>
                  <div className="md:col-span-7 md:col-start-6">
                    <h3 className="font-serif text-3xl md:text-5xl font-medium tracking-tight">{c.title}</h3>
                    <p className="mt-5 text-base sm:text-lg leading-relaxed text-ink/70 max-w-xl">{c.text}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-28 md:py-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <p className="label">Join us</p>
            <h2 className="mt-4 font-serif font-medium text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[1.0] max-w-3xl">
              Give hope a <em className="text-terra">home.</em>
            </h2>
            <p className="mt-8 text-base sm:text-lg leading-relaxed text-ink/70 max-w-xl">
              Every gift stays close to the ground — cooked into meals, bound into books, and carried by local leaders
              who never leave.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/donate" data-testid="home-cta-donate-button" className="btn-primary group">
                Donate now
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link to="/contact" data-testid="home-cta-contact-button" className="btn-outline">
                Talk to us
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
};

export default Home;
